import { useRef, useEffect, useCallback } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger, useGSAP);

// ============================================
// SCROLL REVEAL — replaces AnimatedSection
// ============================================
export function useScrollReveal(options: {
  y?: number;
  x?: number;
  scale?: number;
  duration?: number;
  delay?: number;
  stagger?: number;
  once?: boolean;
} = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const {
    y = 40,
    x = 0,
    scale = 1,
    duration = 0.8,
    delay = 0,
    stagger = 0,
    once = true,
  } = options;

  useGSAP(() => {
    if (!ref.current) return;

    const children = stagger > 0 ? ref.current.children : [ref.current];

    gsap.fromTo(
      children,
      {
        opacity: 0,
        y: y,
        x: x,
        scale: scale !== 1 ? scale : 1,
      },
      {
        opacity: 1,
        y: 0,
        x: 0,
        scale: 1,
        duration,
        delay,
        stagger: stagger > 0 ? stagger : 0,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 85%",
          end: "bottom 20%",
          toggleActions: once ? "play none none none" : "play reverse play reverse",
        },
      }
    );
  }, { scope: ref });

  return ref;
}

// ============================================
// PARALLAX — background element parallax
// ============================================
export function useParallax(speed: number = 0.3) {
  const ref = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!ref.current) return;

    gsap.to(ref.current, {
      y: () => speed * 100,
      ease: "none",
      scrollTrigger: {
        trigger: ref.current,
        start: "top bottom",
        end: "bottom top",
        scrub: true,
      },
    });
  }, { scope: ref });

  return ref;
}

// ============================================
// SPLIT TEXT REVEAL — for headings
// ============================================
export function useSplitReveal(options: {
  type?: "chars" | "words" | "lines";
  duration?: number;
  stagger?: number;
  delay?: number;
} = {}) {
  const ref = useRef<HTMLElement>(null);
  const {
    type = "words",
    duration = 0.6,
    stagger = 0.05,
    delay = 0,
  } = options;

  useGSAP(() => {
    if (!ref.current) return;

    const text = ref.current.textContent || "";
    const parts = type === "chars"
      ? text.split("")
      : type === "words"
        ? text.split(" ")
        : [text];

    ref.current.innerHTML = parts
      .map((part) => {
        const content = type === "words" ? part + " " : part;
        return `<span style="display:inline-block;overflow:hidden;"><span class="split-inner" style="display:inline-block;">${content}</span></span>`;
      })
      .join("");

    const inners = ref.current.querySelectorAll(".split-inner");

    gsap.fromTo(
      inners,
      { y: "110%", opacity: 0 },
      {
        y: "0%",
        opacity: 1,
        duration,
        stagger,
        delay,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 85%",
          toggleActions: "play none none none",
        },
      }
    );
  }, { scope: ref });

  return ref;
}

// ============================================
// MAGNETIC HOVER — for buttons and links
// ============================================
export function useMagnetic(strength: number = 0.3) {
  const ref = useRef<HTMLElement>(null);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const x = e.clientX - rect.left - rect.width / 2;
      const y = e.clientY - rect.top - rect.height / 2;

      gsap.to(ref.current, {
        x: x * strength,
        y: y * strength,
        duration: 0.4,
        ease: "power2.out",
      });
    },
    [strength]
  );

  const handleMouseLeave = useCallback(() => {
    if (!ref.current) return;
    gsap.to(ref.current, {
      x: 0,
      y: 0,
      duration: 0.6,
      ease: "elastic.out(1, 0.3)",
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener("mousemove", handleMouseMove);
    el.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      el.removeEventListener("mousemove", handleMouseMove);
      el.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [handleMouseMove, handleMouseLeave]);

  return ref;
}

// ============================================
// COUNTER — animate numbers counting up
// ============================================
export function useCounter(end: number, duration: number = 2) {
  const ref = useRef<HTMLElement>(null);

  useGSAP(() => {
    if (!ref.current) return;

    const obj = { val: 0 };
    gsap.to(obj, {
      val: end,
      duration,
      ease: "power2.out",
      scrollTrigger: {
        trigger: ref.current,
        start: "top 85%",
        toggleActions: "play none none none",
      },
      onUpdate: () => {
        if (ref.current) {
          ref.current.textContent = Math.round(obj.val).toLocaleString();
        }
      },
    });
  }, { scope: ref });

  return ref;
}

// ============================================
// STAGGER CHILDREN — reveal list items
// ============================================
export function useStaggerReveal(options: {
  stagger?: number;
  y?: number;
  duration?: number;
  delay?: number;
} = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const { stagger = 0.1, y = 30, duration = 0.6, delay = 0 } = options;

  useGSAP(() => {
    if (!ref.current) return;

    gsap.fromTo(
      ref.current.children,
      { opacity: 0, y },
      {
        opacity: 1,
        y: 0,
        duration,
        stagger,
        delay,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 85%",
          toggleActions: "play none none none",
        },
      }
    );
  }, { scope: ref });

  return ref;
}

// ============================================
// FLOATING ANIMATION — organic float effect
// ============================================
export function useFloat(options: {
  yRange?: number;
  xRange?: number;
  rotateRange?: number;
  duration?: number;
} = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const { yRange = 15, xRange = 5, rotateRange = 3, duration = 4 } = options;

  useGSAP(() => {
    if (!ref.current) return;

    gsap.to(ref.current, {
      y: `random(-${yRange}, ${yRange})`,
      x: `random(-${xRange}, ${xRange})`,
      rotation: `random(-${rotateRange}, ${rotateRange})`,
      duration: duration,
      ease: "sine.inOut",
      repeat: -1,
      yoyo: true,
    });
  }, { scope: ref });

  return ref;
}
